import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTenant } from "@/lib/tenant";
import { ArrowLeft, Fuel, Droplets, AlertCircle, Loader2, Activity, Gauge } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const METRICS = [
  { key: "fuel",        label: "Fuel %",    color: "#fbbf24" },
  { key: "def",         label: "DEF %",     color: "#34d399" },
  { key: "engine_load", label: "Engine load %", color: "#60a5fa" },
  { key: "speed",       label: "Speed (kph)", color: "#f472b6" },
];

export default function FleetMachine() {
  const { vin } = useParams();
  const { api } = useTenant();
  const [eq, setEq] = useState(null);
  const [faults, setFaults] = useState([]);
  const [metric, setMetric] = useState("fuel");
  const [series, setSeries] = useState([]);
  const [hours, setHours] = useState(8);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/fieldops/equipment/${vin}`),
      api.get(`/fieldops/equipment/${vin}/faults`),
    ]).then(([e, f]) => {
      // Response is {data, source} — unwrap. Equipment data could be a single dict.
      const eqPayload = e.data;
      setEq(eqPayload?.data && typeof eqPayload.data === "object" ? eqPayload.data : eqPayload);
      const fPayload = f.data;
      setFaults(Array.isArray(fPayload) ? fPayload : (fPayload?.data || []));
    })
      .finally(() => setLoading(false));
  }, [vin]); // eslint-disable-line

  useEffect(() => {
    api.get(`/fieldops/equipment/${vin}/telemetry`, { params: { metric, hours } })
      .then(r => setSeries(r.data.points || []))
      .catch(() => setSeries([]));
  }, [vin, metric, hours]); // eslint-disable-line

  if (loading || !eq) {
    return (
      <div className="p-8 flex items-center text-slate-500 text-sm">
        <Loader2 className="animate-spin mr-2" size={16} /> Loading machine…
      </div>
    );
  }

  const chartData = series.map(p => ({ t: new Date(p.timestamp || p.datetime || p.t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }), v: p.value ?? p.v ?? p.Percent ?? p.Hour ?? p.Speed }));
  const metricColor = METRICS.find(m => m.key === metric)?.color || "#fbbf24";
  const fmt = (v, suffix = "") => (typeof v === "number" ? `${v}${suffix}` : "—");
  const fmtH = (v) => (typeof v === "number" ? `${v.toFixed(1)}h` : "—");

  return (
    <div className="p-8 max-w-7xl mx-auto">
      <Link to="/admin/fleet" data-testid="back-to-fleet"
            className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-amber-300 mb-5">
        <ArrowLeft size={14} /> Back to fleet
      </Link>

      <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-amber-300/80">{eq.category || "Tractor"}</div>
            <h1 className="font-display font-black text-3xl text-slate-100 mt-0.5">{eq.nickname || eq.vin}</h1>
            <div className="text-sm text-slate-400 mt-1">{eq.make || ""} {eq.model || ""}{eq.year ? ` · ${eq.year}` : ""} · VIN {eq.vin}</div>
            {eq.serial && <div className="text-xs text-slate-500 mt-2">Serial {eq.serial}</div>}
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Operating hours</div>
            <div className="text-3xl font-display font-black text-amber-300 tabular-nums">{fmtH(eq.operating_hours)}</div>
            {typeof eq.next_service_due_hours === "number" && typeof eq.operating_hours === "number" && (
              <div className="text-xs text-slate-500 mt-1">Next service in {Math.max(0, eq.next_service_due_hours - eq.operating_hours).toFixed(0)}h</div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
          {[
            { i: Fuel,     l: "Fuel",       v: fmt(eq.fuel_remaining_pct, "%") },
            { i: Droplets, l: "DEF",        v: fmt(eq.def_remaining_pct, "%") },
            { i: Gauge,    l: "Engine load",v: fmt(eq.engine_load_pct, "%") },
            { i: Activity, l: "Speed",      v: fmt(eq.speed_kph, " kph") },
          ].map(s => {
            const SIcon = s.i;
            return (
              <div key={s.l} className="bg-white/[0.03] border border-white/5 rounded p-3">
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500"><SIcon size={11} /> {s.l}</div>
                <div className="text-lg font-display font-bold text-slate-100 tabular-nums mt-1">{s.v}</div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Telemetry chart */}
      <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-6 mb-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex gap-1">
            {METRICS.map(m => (
              <button key={m.key} onClick={() => setMetric(m.key)} data-testid={`metric-${m.key}`}
                      className={`px-3 py-1.5 text-xs rounded border transition-colors ${metric === m.key ? "bg-amber-400 text-[#0a0e1c] border-amber-400 font-semibold" : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10"}`}>
                {m.label}
              </button>
            ))}
          </div>
          <select value={hours} onChange={(e) => setHours(parseInt(e.target.value))}
                  data-testid="hours-select"
                  className="bg-white/5 border border-white/10 text-slate-200 text-xs rounded px-2 py-1.5">
            <option value={4}>Last 4h</option>
            <option value={8}>Last 8h</option>
            <option value={24}>Last 24h</option>
          </select>
        </div>
        <div style={{ height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
              <XAxis dataKey="t" stroke="#64748b" fontSize={11} tickLine={false} />
              <YAxis stroke="#64748b" fontSize={11} tickLine={false} />
              <Tooltip contentStyle={{ background: "#0a0e1c", border: "1px solid #334155", borderRadius: 6, fontSize: 12 }} />
              <Line type="monotone" dataKey="v" stroke={metricColor} strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Faults */}
      <div className="bg-[#0a0e1c] border border-white/10 rounded-lg p-6">
        <h2 className="text-sm font-semibold text-slate-200 mb-3 flex items-center gap-2"><AlertCircle size={14} className="text-red-400" /> Fault codes</h2>
        {faults.length === 0 ? (
          <div className="text-xs text-slate-500 py-4">No active faults — all systems nominal.</div>
        ) : (
          <ul className="space-y-2">
            {faults.map((f, idx) => (
              <li key={f.code || f.spn || idx} data-testid={`fault-${f.code || idx}`}
                  className={`flex items-start gap-3 px-3 py-2.5 rounded border ${f.severity === "critical" ? "bg-red-500/10 border-red-500/30" : f.severity === "warning" ? "bg-amber-500/10 border-amber-500/30" : "bg-white/[0.03] border-white/5"}`}>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 rounded ${f.severity === "critical" ? "bg-red-500/30 text-red-200" : f.severity === "warning" ? "bg-amber-500/30 text-amber-200" : "bg-slate-500/30 text-slate-200"}`}>{f.severity || "fault"}</span>
                <div className="flex-1">
                  <div className="text-sm text-slate-200 font-mono">{f.code || f.spn || f.fmi || "—"}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{f.description || f.message || ""}</div>
                  {(f.first_seen || f.datetime) && <div className="text-[10px] text-slate-500 mt-0.5">First seen {new Date(f.first_seen || f.datetime).toLocaleString("en-GB")}</div>}
                </div>
                {f.active && <span className="text-[10px] uppercase tracking-widest text-red-300 font-bold">Active</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
